import type { IndexableRecord } from '../types';
import {
  JobsInquiryAeQuota,
  JobsInquiryCsmAccountNumber,
  JobsInquiryCsmBusinessValue,
  JobsInquiryCsmQuota,
  JobsInquiryCsmQuotaMetric,
  JobsInquiryCsmQuotaPeriod,
  JobsInquiryCsmRenewalValue,
  JobsInquiryPosition,
  JobsInquiryVpDepartmentSize,
  JobsInquiryVpManagedRole,
  JobsInquiryVpQuota,
} from '@/types/graphql';

export const jobsInquiryRoleNames = Object.freeze({
  [JobsInquiryPosition.AccountExecutive]: 'Account Executive',
  [JobsInquiryPosition.Vp]: 'Director, Manager, VP, CRO',
  [JobsInquiryPosition.Sdr]: 'SDR, BDR, MDR, etc.',
  [JobsInquiryPosition.Csm]: 'CSM, AM',
});

export const jobsAeQuotaPeriodNames = Object.freeze({
  [JobsInquiryAeQuota.Monthly]: 'Monthly',
  [JobsInquiryAeQuota.Quarterly]: 'Quarterly',
  [JobsInquiryAeQuota.Annual]: 'Annual',
});

export const jobsCsmQuotaPeriodNames = Object.freeze({
  [JobsInquiryCsmQuotaPeriod.Monthly]: 'Monthly',
  [JobsInquiryCsmQuotaPeriod.Quarterly]: 'Quarterly',
  [JobsInquiryCsmQuotaPeriod.Annual]: 'Annual',
});

export const csmBusinessValueTitles: IndexableRecord = Object.freeze({
  [JobsInquiryCsmBusinessValue.ValueLess_250KUsd]: '$0 to $250k',
  [JobsInquiryCsmBusinessValue.Value_251K_1MUsd]: '$251k to $1M',
  [JobsInquiryCsmBusinessValue.Value_1_1M_2MUsd]: '$1.1 to $2M',
  [JobsInquiryCsmBusinessValue.Value_2_1M_5MUsd]: '$2.1 to $5M',
  [JobsInquiryCsmBusinessValue.ValueMore_5MUsd]: 'More than $5M',
});

export const csmAccountCountTitles: IndexableRecord = Object.freeze({
  [JobsInquiryCsmAccountNumber.Number_1_10]: '1 to 10',
  [JobsInquiryCsmAccountNumber.Number_11_50]: '10 to 50',
  [JobsInquiryCsmAccountNumber.Number_51_100]: '50 to 100',
  [JobsInquiryCsmAccountNumber.Number_101_250]: '100 to 250',
  [JobsInquiryCsmAccountNumber.Number_251_500]: '250 to 500',
  [JobsInquiryCsmAccountNumber.NumberMore_500]: 'More than 500',
});

export const csmQuotaTypeTitles = Object.freeze({
  [JobsInquiryCsmQuota.CsmTeamQuota]: 'Team quota',
  [JobsInquiryCsmQuota.CsmIndividualQuota]: 'Individual',
  [JobsInquiryCsmQuota.CsmNoQuota]: 'I don’t have a quota',
});

export const csmQuotaMetricTitles: IndexableRecord = Object.freeze({
  [JobsInquiryCsmQuotaMetric.CsmRenewals]: 'Renewals',
  [JobsInquiryCsmQuotaMetric.CsmUpsells]: 'Upsells',
  [JobsInquiryCsmQuotaMetric.CsmOpportunities]: 'Qualified Opportunities',
  [JobsInquiryCsmQuotaMetric.CsmEngagement]: 'Engagement',
  [JobsInquiryCsmQuotaMetric.CsmCustomerSatisfaction]: 'Customer Satisfaction',
});

export const csmRenewalValueTitles: IndexableRecord = Object.freeze({
  [JobsInquiryCsmRenewalValue.ValueLess_1KUsd]: 'Less than $1,000 / year',
  [JobsInquiryCsmRenewalValue.Value_1K_5KUsd]: '$1,000 – $5,000 / year',
  [JobsInquiryCsmRenewalValue.Value_5K_25KUsd]: '$5,000 – $25,000 / year',
  [JobsInquiryCsmRenewalValue.Value_25K_100KUsd]: '$25,000 – $100,000 / year',
  [JobsInquiryCsmRenewalValue.ValueMore_100KUsd]: 'More than $100,000 / year',
});

export const vpDepartmentSizes: IndexableRecord = Object.freeze({
  [JobsInquiryVpDepartmentSize.VpDepartmentSize_1_20]: '1 to 20',
  [JobsInquiryVpDepartmentSize.VpDepartmentSize_21_50]: '20 to 50',
  [JobsInquiryVpDepartmentSize.VpDepartmentSize_51_75]: '50 to 75',
  [JobsInquiryVpDepartmentSize.VpDepartmentSize_76_100]: '75 to 100',
  [JobsInquiryVpDepartmentSize.VpDepartmentSizeMore_100]: 'More than 100',
});

export const vpQuotaTypes = Object.freeze({
  [JobsInquiryVpQuota.VpQuotaIndividual]: 'Individually',
  [JobsInquiryVpQuota.VpQuotaTeam]: 'Per team',
});

export const vpManagedRoles: IndexableRecord = Object.freeze({
  [JobsInquiryVpManagedRole.VpManagedRoleSdr]: 'SDRs',
  [JobsInquiryVpManagedRole.VpManagedRoleAe]: 'AEs',
  [JobsInquiryVpManagedRole.VpManagedRoleCsm]: 'CSMs',
  [JobsInquiryVpManagedRole.VpManagedRoleSe]: 'SEs',
  [JobsInquiryVpManagedRole.VpManagedRoleSalesOps]: 'Sales Ops',
  [JobsInquiryVpManagedRole.VpManagedRoleSalesEnablement]: 'Sales Enablement',
  [JobsInquiryVpManagedRole.VpManagedRoleSdrManager]: 'SDR Managers',
  [JobsInquiryVpManagedRole.VpManagedRoleAeManager]: 'AE Managers',
  [JobsInquiryVpManagedRole.VpManagedRoleDirector]: 'Directors',
  [JobsInquiryVpManagedRole.VpManagedRoleOther]: 'Other',
});
